import React from "react";
import { apiClient } from "../../lib/api";

// ActivityLogPanel — canonical audit trail viewer.
// Phase 3 observability: the compliance officer or auditor must be able to
// answer "who did what, when" without shell access.

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

const ROLE_FILTERS = [
  { v: "", label: "All roles" },
  { v: "admin", label: "Admin" },
  { v: "va", label: "VA" },
  { v: "dispatcher", label: "Dispatcher" },
  { v: "tp_owner", label: "TP Owner" },
  { v: "system", label: "System" },
];

const EVENT_PRESETS = [
  { v: "", label: "Any event" },
  { v: "internal_user.", label: "User admin" },
  { v: "organization.", label: "Orgs" },
  { v: "trip.", label: "Trips" },
  { v: "invoice.", label: "Invoices" },
  { v: "sos.", label: "SOS" },
  { v: "auth.", label: "Auth" },
  { v: "broker.", label: "Broker ingest" },
];

const roleTone = (r) => {
  switch ((r || "").toLowerCase()) {
    case "admin":       return "bg-forest/10 text-forest";
    case "va":          return "bg-slate-100 text-slate_navy";
    case "dispatcher":  return "bg-amber-50 text-amber-700";
    case "tp_owner":    return "bg-purple-50 text-purple-700";
    case "system":      return "bg-slate-200 text-slate-600";
    default:            return "bg-slate-100 text-slate-500";
  }
};

const eventTone = (e) => {
  const s = (e || "").toLowerCase();
  if (s.includes("deleted") || s.includes("deactivated") || s.includes("cancel") || s.includes("refund"))
    return "bg-rust/10 text-rust";
  if (s.includes("created") || s.includes("issued") || s.includes("approved"))
    return "bg-forest/10 text-forest";
  if (s.includes("updated") || s.includes("changed"))
    return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-600";
};

export default function ActivityLogPanel() {
  const [role, setRole] = React.useState("");
  const [eventPrefix, setEventPrefix] = React.useState("");
  const [limit, setLimit] = React.useState(100);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [expanded, setExpanded] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit };
      if (role) params.actor_role = role;
      if (eventPrefix) params.event_prefix = eventPrefix;
      const r = await apiClient.get(`/admin/activity`, { params });
      const body = r.data;
      setRows(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setError(String(e?.response?.data?.error?.message || e?.response?.data?.detail || e?.message || "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [role, eventPrefix, limit]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <section
      className="rounded-2xl border border-[#E2E0D8] bg-white p-5 astat-fade-in"
      data-testid="activity-log-panel"
    >
      <header className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] text-forest/70 uppercase">
            Audit Trail
          </div>
          <h2 className="text-xl font-bold text-slate_navy" data-testid="activity-log-title">
            Activity log
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Every state change on every canonical entity, in the order it happened.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="activity-role-filter"
            className="text-sm border border-[#E2E0D8] rounded-md px-2 py-1.5 bg-white"
          >
            {ROLE_FILTERS.map(f => <option key={f.v} value={f.v}>{f.label}</option>)}
          </select>
          <select
            value={eventPrefix}
            onChange={(e) => setEventPrefix(e.target.value)}
            data-testid="activity-event-filter"
            className="text-sm border border-[#E2E0D8] rounded-md px-2 py-1.5 bg-white"
          >
            {EVENT_PRESETS.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            data-testid="activity-limit"
            className="text-sm border border-[#E2E0D8] rounded-md px-2 py-1.5 bg-white"
          >
            {[50, 100, 250, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            onClick={load}
            data-testid="activity-refresh"
            className="text-sm px-3 py-1.5 border border-[#E2E0D8] rounded-md hover:bg-forest/5"
          >
            ↻ Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-3 rounded-lg border border-rust/50 bg-rust/5 px-3 py-2 text-sm text-rust" data-testid="activity-error">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-[#E2E0D8]">
        <table className="min-w-full text-sm" data-testid="activity-table">
          <thead className="bg-bone/60 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 w-[110px]">When</th>
              <th className="text-left px-3 py-2 w-[180px]">Actor</th>
              <th className="text-left px-3 py-2">Event</th>
              <th className="text-left px-3 py-2 w-[200px]">Target</th>
              <th className="w-[80px]" />
            </tr>
          </thead>
          <tbody className="astat-fade-stagger" key={rows.length + ":" + role + ":" + eventPrefix}>
            {loading && (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400" data-testid="activity-loading">
                Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400" data-testid="activity-empty">
                No entries match these filters.
              </td></tr>
            )}
            {rows.map((r) => {
              const isOpen = expanded === r.id;
              const actor = r.actor || {};
              const entity = r.entity || {};
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-[#E2E0D8] hover:bg-slate-50" data-testid={`activity-row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtWhen(r.at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate_navy">{actor.name || actor.id || "—"}</div>
                      <span className={"inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider " + roleTone(actor.role)}>
                        {actor.role || actor.type || "?"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={"inline-block px-2 py-0.5 rounded-full text-xs font-semibold " + eventTone(r.event)}>
                        {r.event}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      <div className="text-xs uppercase tracking-wider text-slate-400">{entity.type || "—"}</div>
                      <div className="font-mono text-xs truncate max-w-[180px]" title={entity.id || ""}>
                        {entity.id ? `${String(entity.id).slice(0, 12)}…` : "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                        data-testid={`activity-expand-${r.id}`}
                        className="text-xs text-forest hover:underline"
                      >
                        {isOpen ? "Hide" : "Details"}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-bone/30 border-t border-[#E2E0D8]">
                      <td colSpan={5} className="px-3 py-3">
                        <div className="grid md:grid-cols-3 gap-3 text-xs">
                          <DiffBlock label="Before" value={r.before} />
                          <DiffBlock label="After" value={r.after} />
                          <DiffBlock label="Meta" value={r.meta} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        Showing {rows.length} · newest first · read-only
      </div>
    </section>
  );
}

function DiffBlock({ label, value }) {
  const isEmpty = value == null || (typeof value === "object" && Object.keys(value).length === 0);
  return (
    <div className="rounded-lg border border-[#E2E0D8] bg-white p-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {isEmpty ? (
        <div className="text-slate-400 italic">—</div>
      ) : (
        <pre className="whitespace-pre-wrap break-all text-slate_navy font-mono text-[11px] leading-tight">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}
